import React from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Input,
  Pagination,
  Card,
  CardBody,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useClubDataState, useClubDelete, useClubBulkOperations, useClubDataOptimized, useClubManagementState, useClubAuthState } from 'src/hooks/useClubsHooks';
import { Club } from 'src/types/api/Club';
import { VerticalDotsIcon } from 'src/assets/icons/VerticalDotsIcon';
import { SearchIcon } from 'src/assets/icons/SearchIcon';
import ClubRegisterModal from 'src/components/modals/ClubRegisterModal';
import ClubLoginModal from 'src/components/modals/ClubLoginModal';

export default function ClubManagement() {
  const { t } = useTranslation();
  
  const { token, isClub } = useClubAuthState();
  
  React.useEffect(() => {
    if (!token || !isClub) {
      console.log('User not authenticated for club access');
    }
  }, [token, isClub]);
  
  const {
    selectedTab,
    filterValue,
    page,
    rowsPerPage,
    selectedClub,
    selectedClubs,
    isEditModalOpen,
    setSelectedTab,
    setFilterValue,
    setPage,
    setRowsPerPage,
    setSelectedClub,
    setSelectedClubs,
    setIsEditModalOpen,
    addSelectedClub,
    removeSelectedClub,
    clearSelectedClubs,
  } = useClubManagementState();

  React.useEffect(() => {
    return () => {
      clearSelectedClubs();
    };
  }, [clearSelectedClubs]);
  
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const { data: optimizedData, isLoading: isOptimizedLoading, error: optimizedError, isError: isOptimizedError } = useClubDataOptimized(
    selectedTab as 'my-clubs' | 'saff-clubs' | 'test-clubs'
  );

  const deleteClubMutation = useClubDelete();
  const bulkOperationsMutation = useClubBulkOperations();

  const debouncedSearch = React.useCallback(
    React.useMemo(
      () => {
        let timeoutId: NodeJS.Timeout;
        return (value: string) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            setFilterValue(value);
          }, 300);
        };
      },
      [setFilterValue]
    ),
    [setFilterValue]
  );

  const handleSearchChange = (value: string) => {
    debouncedSearch(value);
  };

  const getCurrentClubs = () => {
    if (optimizedData && 'clubs' in optimizedData) {
      return optimizedData.clubs || [];
    }
    
    if (isOptimizedError) {
      return [];
    }
    
    return [];
  };

  const getTotalClubs = () => {
    if (optimizedData && 'totalClubs' in optimizedData) {
      return optimizedData.totalClubs || 0;
    }
    return 0;
  };

  const getIsLoading = () => {
    if (isOptimizedLoading !== undefined) {
      return isOptimizedLoading;
    }
    
    if (isOptimizedError) {
      return false;
    }
    
    return false;
  };

  const handleEditClub = (club: Club) => {
    setSelectedClub(club);
    onOpen();
  };

  const handleDeleteClub = async (club: Club) => {
    try {
      await deleteClubMutation.mutateAsync(club.club_id);
    } catch (error) {
      console.error('Failed to delete club:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClubs.size === 0) return;
    
    const operations = Array.from(selectedClubs).map(clubId => ({
      action: 'delete',
      clubId
    }));

    try {
      await bulkOperationsMutation.mutateAsync(operations);
      clearSelectedClubs();
    } catch (error) {
      console.error('Failed to perform bulk delete:', error);
    }
  };

  const handleSelectClub = (clubId: string, isSelected: boolean) => {
    if (isSelected) {
      addSelectedClub(clubId);
    } else {
      removeSelectedClub(clubId);
    }
  };

  const renderActions = (club: Club) => (
    <Dropdown>
      <DropdownTrigger>
        <Button isIconOnly size="sm" variant="light">
          <VerticalDotsIcon className="text-default-300" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu>
        <DropdownItem onClick={() => handleEditClub(club)}>
          {t('edit')}
        </DropdownItem>
        <DropdownItem 
          className="text-danger" 
          color="danger"
          onClick={() => handleDeleteClub(club)}
        >
          {t('delete')}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );

  const renderClubStatus = (club: Club) => (
    <Chip
      color={club.is_club ? "success" : "default"}
      variant="flat"
      size="sm"
    >
      {club.is_club ? t('club.status.active') : t('club.status.inactive')}
    </Chip>
  );

  const columns = [
    { name: '', uid: 'select' },
    { name: t('club.name'), uid: 'name' },
    { name: t('club.person.name'), uid: 'person_name' },
    { name: t('club.mobile'), uid: 'mobile' },
    { name: t('club.email'), uid: 'email' },
    { name: t('club.username'), uid: 'username' },
    { name: t('club.status'), uid: 'status' },
    { name: t('club.created_at'), uid: 'created_at' },
    { name: t('actions'), uid: 'actions' },
  ];

  const renderCell = (club: Club, columnKey: React.Key) => {
    switch (columnKey) {
      case 'select':
        return (
          <input
            type="checkbox"
            checked={selectedClubs.has(club.club_id)}
            onChange={(e) => handleSelectClub(club.club_id, e.target.checked)}
            className="w-4 h-4"
          />
        );
      case 'name':
        return (
          <div className="flex items-center gap-2">
            {club.avatar && (
              <Avatar
                src={club.avatar}
                size="sm"
                name={club.club_name_en}
              />
            )}
            <div>
              <p className="font-semibold">{club.club_name_en}</p>
              <p className="text-sm text-gray-500">{club.club_name_ar}</p>
            </div>
          </div>
        );
      case 'person_name':
        return (
          <div>
            <p className="font-medium">{club.person_name_en}</p>
            <p className="text-sm text-gray-500">{club.person_name_ar}</p>
          </div>
        );
      case 'mobile':
        return club.mobile;
      case 'email':
        return club.email;
      case 'username':
        return club.username;
      case 'status':
        return renderClubStatus(club);
      case 'created_at':
        return new Date(club.created_at).toLocaleDateString();
      case 'actions':
        return renderActions(club);
      default:
        return null;
    }
  };

  const pages = Math.ceil(getTotalClubs() / rowsPerPage);

  if (!token || !isClub) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{t('club.management.title')}</h1>
          <div className="flex gap-2">
            <ClubLoginModal />
            <ClubRegisterModal />
          </div>
        </div>
        <Card>
          <CardBody className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t('club.auth.required')}</h2>
            <p className="text-gray-600 mb-6">{t('club.auth.required.message')}</p>
            <div className="flex justify-center gap-4">
              <ClubLoginModal />
              <ClubRegisterModal />
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('club.management.title')}</h1>
        <div className="flex gap-2">
          <ClubLoginModal />
          <ClubRegisterModal />
        </div>
      </div>

      <Card>
        <CardBody>
          <Tabs 
            selectedKey={selectedTab} 
            onSelectionChange={(key) => setSelectedTab(key as 'my-clubs' | 'saff-clubs' | 'test-clubs')}
            aria-label="Club tabs"
            className="mb-4"
          >
            <Tab key="my-clubs" title={t('club.tabs.my_clubs')} />
            <Tab key="saff-clubs" title={t('club.tabs.saff_clubs')} />
            <Tab key="test-clubs" title={t('club.tabs.test_clubs')} />
          </Tabs>

          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 items-center">
              <Input
                isClearable
                className="w-full sm:max-w-[44%]"
                placeholder={t('search.clubs')}
                startContent={<SearchIcon />}
                value={filterValue}
                onClear={() => handleSearchChange('')}
                onValueChange={handleSearchChange}
              />
              {selectedClubs.size > 0 && (
                <Button
                  color="danger"
                  variant="flat"
                  onPress={handleBulkDelete}
                  isLoading={bulkOperationsMutation.isPending}
                >
                  {t('delete.selected')} ({selectedClubs.size})
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                color="primary"
                variant="flat"
                onPress={() => setPage(1)}
                isDisabled={page === 1}
              >
                {t('first')}
              </Button>
              <Button
                color="primary"
                variant="flat"
                onPress={() => setPage(page - 1)}
                isDisabled={page === 1}
              >
                {t('previous')}
              </Button>
              <Button
                color="primary"
                variant="flat"
                onPress={() => setPage(page + 1)}
                isDisabled={page === pages}
              >
                {t('next')}
              </Button>
              <Button
                color="primary"
                variant="flat"
                onPress={() => setPage(pages)}
                isDisabled={page === pages}
              >
                {t('last')}
              </Button>
            </div>
          </div>

          {optimizedError && (
            <div className="p-4 text-center text-red-600 mb-4">
              Error loading clubs: {optimizedError.message}
            </div>
          )}
          
          <Table
            aria-label="Clubs table"
            isHeaderSticky
            classNames={{
              wrapper: "max-h-[600px]",
            }}
          >
            <TableHeader columns={columns}>
              {(column) => (
                <TableColumn key={column.uid} align="start">
                  {column.name}
                </TableColumn>
              )}
            </TableHeader>
            <TableBody
              items={getCurrentClubs()}
              isLoading={getIsLoading()}
              loadingContent={t('loading')}
              emptyContent={t('no.clubs.found')}
            >
              {(club) => (
                <TableRow key={club.user_id}>
                  {(columnKey) => (
                    <TableCell>{renderCell(club, columnKey)}</TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-gray-500">
              {t('showing')} {((page - 1) * rowsPerPage) + 1} {t('to')} {Math.min(page * rowsPerPage, getTotalClubs())} {t('of')} {getTotalClubs()} {t('clubs')}
            </p>
            <Pagination
              isCompact
              showControls
              showShadow
              color="primary"
              page={page}
              total={pages}
              onChange={setPage}
            />
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{t('edit.club')}</ModalHeader>
              <ModalBody>
                {selectedClub && (
                  <div className="space-y-4">
                    <p><strong>{t('club.name.en')}:</strong> {selectedClub.club_name_en}</p>
                    <p><strong>{t('club.name.ar')}:</strong> {selectedClub.club_name_ar}</p>
                    <p><strong>{t('club.person.name.en')}:</strong> {selectedClub.person_name_en}</p>
                    <p><strong>{t('club.person.name.ar')}:</strong> {selectedClub.person_name_ar}</p>
                    <p><strong>{t('club.mobile')}:</strong> {selectedClub.mobile}</p>
                    <p><strong>{t('club.email')}:</strong> {selectedClub.email}</p>
                    <p><strong>{t('club.username')}:</strong> {selectedClub.username}</p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  {t('cancel')}
                </Button>
                <Button color="primary" onPress={onClose}>
                  {t('save')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
